/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Component: Footer
 * File     : Footer.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Reusable React UI component: Footer, composed within pages and other components.
 *   Exports: Footer
 *   
 *
 * Dependencies:
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Import: import Footer from '@/components/layout/Footer';
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  return (
    <Box component="footer" sx={{ mt: 6, py: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Container>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
          <Typography variant="body2" color="text.secondary">© {year} ReelConsensus. All rights reserved.</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Link component="button" variant="body2" onClick={() => navigate('/privacy-policy')} color="inherit">Privacy Policy</Link>
            <Link component="button" variant="body2" onClick={() => navigate('/terms-and-conditions')} color="inherit">Terms and Conditions</Link>
            <Link component="button" variant="body2" onClick={() => navigate('/sitemap')} color="inherit">Sitemap</Link>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              GDPR: Request data deletion via Admin or contact info@reelconsensus.com
            </Typography>
            {/* Discord small logo link */}
            <Link href="https://discord.gg/vRrMdZ9dDr" target="_blank" rel="noopener noreferrer" aria-label="Join us on Discord" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg"
      width="28" height="28"
      viewBox="0 0 24 24"
      role="img" aria-label="Discord logo"
      fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>

            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;


